import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {buildCpWidgetForm} from '../../../../../service/domain/cp-widget-column';

/**
 * navItem "Buchungen" of the Provisionssatz OBC, widget alias
 * `cp.contractAgent.transactions.table`.
 *
 * The contract-side journal of ONE Provisionssatz: every `cp.ProvisionContractTransaction` whose
 * `provisionContractAgent` is this row. That is the pairing side of the account booking — a
 * contract booking always exists together with its account counterpart, while a manual account
 * booking may stand alone; rooting here is therefore correct for the question "what was booked ON
 * THIS Satz".
 *
 * A `provisionRun` of NULL marks a manual contract-related correction (event MANUAL_ADJUSTMENT),
 * which is exactly why the run column is part of the curated layout.
 *
 * The table is a QL view with the same shape as the sibling panes of this module
 * (`cp-provision-agent-reserve-coverage.component.ts`); the curated column set goes through
 * {@link buildCpWidgetForm}, without which the generic QL table would render every projected
 * attribute.
 *
 * REFRESH: bookings are written by the calculation run, so the pane subscribes to
 * `JbSpringBatchIndicatorService.status$` and reloads IN PLACE on the running -> not-running
 * transition (sorting and scroll position survive). It also listens to `objectChangeEvent` for
 * manual corrections booked while the pane is open.
 */
@Component({
    selector: 'cp-provision-contract-agent-transactions',
    templateUrl: './cp-provision-contract-agent-transactions.component.html',
    styleUrls: ['./cp-provision-contract-agent-transactions.component.scss'],
    standalone: false
})
export class CpProvisionContractAgentTransactionsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionContractAgentId: number;

    transactionsWidget: WidgetData;

    /** Is a cp job currently running? */
    jobRunning: boolean;

    /**
     * The mounted widget instance — only for the in-place reload. `refreshWidget()` reloads
     * WITHOUT touching the binding; a freshly assigned `transactionsWidget` would remount the
     * table and lose sorting and scroll position.
     */
    @ViewChild('transactionsWidgetRef') transactionsWidgetComponent: MvsWidgetComponent;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /** Reload as soon as a contract booking was written. */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionContractTransaction') {
                this.reloadInPlace();
            }
        });
    }

    /** Reload on the running -> not-running transition of a cp job. */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.reloadInPlace();
            }
        });
    }

    /** Reloads the table in place; builds one only if none exists yet. */
    protected reloadInPlace(): void {
        if (this.transactionsWidgetComponent) {
            this.transactionsWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.provisionContractAgentId) {
            this.transactionsWidget = null;
            this.initialized = true;
            return;
        }

        const qlQuery: QlQueryDto = {
            name: 'cp.contractAgent.transactions',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionContractTransaction',
                as: 'e',
                joins: [
                    {
                        type: 'entity', name: 'contractTransactionType', as: 'ctt', joinType: 'left',
                        fields: [{name: 'name'}]
                    },
                    {
                        type: 'entity', name: 'provisionRun', as: 'pr', joinType: 'left',
                        fields: [{name: 'valutaDate'}, {name: 'status'}]
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionContractAgentId',
                    FilterCriteria.cOperatorEqual, this.provisionContractAgentId)
            ],
            sortings: [
                new Sorting('e.valutaDate', false),
                new Sorting('e.id', false)
            ]
        };

        /**
         * AI: Contract-side bookings of one Provisionssatz.
         *     Source: QL view on cp.ProvisionContractTransaction, joined to the booking type and
         *     the generating provision run. An empty run column marks a manual correction.
         */
        this.transactionsWidget = WidgetFactory.createWidgetTableQl(
            'cp.contractAgent.transactions.table',
            'Buchungen zu diesem Provisionssatz',
            qlQuery,
            WidgetDataParam.create('paging', 25),
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('widgetForm', buildCpWidgetForm([
                {field: 'e.valutaDate', label: 'Valutadatum'},
                {field: 'ctt.name', label: 'Buchungsart'},
                {field: 'e.event', label: 'Ereignis'},
                {field: 'e.amount', label: 'Betrag'},
                {field: 'e.status', label: 'Status'},
                {field: 'pr.valutaDate', label: 'Lauf (Valuta)'},
                {field: 'pr.status', label: 'Laufstatus'},
                {field: 'e.referenceText', label: 'Referenz'}
            ]))
        );

        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionContractAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
